// SSO Authentication Hook
import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URLS } from '../constants';

export type SSOProvider = 'google' | 'microsoft';

interface SSOState {
  loading: boolean;
  error: string | null;
}

interface SSOCallbackResult {
  success: boolean;
  error?: string;
  hasValidToken?: boolean;
  pendingAuth?: boolean;
}

interface SSOConfig {
  google: {
    enabled: boolean;
    clientId: string;
  };
  microsoft: {
    enabled: boolean;
    clientId: string;
  };
}

export const useSSO = () => {
  const { checkSession } = useAuth();
  const [ssoState, setSSOState] = useState<Record<SSOProvider, SSOState>>({
    google: { loading: false, error: null },
    microsoft: { loading: false, error: null },
  });

  const getSSOConfig = useCallback(async (): Promise<SSOConfig | null> => {
    try {
      const response = await fetch(API_URLS.AUTH.SSO.CONFIG);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch SSO config: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      // Handle both old direct response and new {success, sso} format
      if (data.success && data.sso) {
        return data.sso;
      }
      // Fallback for direct response format
      return data.sso || data;
    } catch (error) {
      console.error('[SSO Hook] Failed to fetch SSO config:', error);
      return null;
    }
  }, []);

  const authenticateWithSSO = useCallback((provider: SSOProvider, forceAccountSelection: boolean = false) => {

    setSSOState(prev => ({
      ...prev,
      [provider]: { loading: true, error: null }
    }));

    try {
      const returnUrl = window.location.pathname;
      localStorage.setItem('sso_return_url', returnUrl);
      
      // Backend-first approach: Same flow for ALL providers
      let ssoUrl: string;
      if (provider === 'google') {
        ssoUrl = API_URLS.AUTH.SSO.GOOGLE;
      } else if (provider === 'microsoft') {
        ssoUrl = API_URLS.AUTH.SSO.MICROSOFT;
      } else {
        throw new Error(`Unsupported SSO provider: ${provider}`);
      }
      
      if (forceAccountSelection) {
        ssoUrl += '?force=true';
      }
      
      // Redirect to backend SSO start
      window.location.href = ssoUrl;
    } catch (error) {
      console.error(`[SSO Hook] ${provider} authentication error:`, error);
      setSSOState(prev => ({
        ...prev,
        [provider]: { 
          loading: false, 
          error: error instanceof Error ? error.message : 'SSO authentication failed' 
        }
      }));
    }
  }, []);

  const handleSSOCallback = useCallback(async (): Promise<SSOCallbackResult> => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    if (error) {
      return { success: false, error: decodeURIComponent(error) };
    }

    // Handle exchange code for secure authentication
    if (code) {
      try {
        // Clean URL first to remove sensitive data
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Exchange code for authentication
        const response = await fetch('/api/sso/exchange', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        });

        if (response.ok) {
          // Wait for cookie propagation
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Check session to verify authentication
          const sessionValid = await checkSession();
          
          return { 
            success: sessionValid, 
            hasValidToken: true,
            pendingAuth: !sessionValid
          };
        } else {
          const errorData = await response.json();
          return { success: false, error: errorData.message || 'Exchange failed' };
        }
      } catch (error) {
        return { success: false, error: 'Failed to process authentication' };
      }
    }

    // Fallback path when no exchange code is present:
    // try a short series of session checks before surfacing an error, and
    // indicate pendingAuth so the UI keeps polling instead of showing error immediately.
    for (let i = 0; i < 3; i++) {
      const ok = await checkSession();
      if (ok) {
        window.history.replaceState({}, document.title, window.location.pathname);
        return { success: true };
      }
      await new Promise(r => setTimeout(r, 500));
    }

    // No exchange code in URL and session not yet validated: signal pending auth to trigger polling
    return { success: false, pendingAuth: true };
  }, [checkSession]);

  const handleAccountLinking = useCallback(async (provider: SSOProvider, email: string) => {
    try {
      
      const linkingUrl = `/auth/link?provider=${provider}&email=${encodeURIComponent(email)}`;
      window.location.href = linkingUrl;
    } catch (error) {
      console.error('[SSO Hook] Account linking error:', error);
    }
  }, []);

  const clearSSOError = useCallback((provider: SSOProvider) => {
    
    setSSOState(prev => ({
      ...prev,
      [provider]: { ...prev[provider], error: null }
    }));
  }, []);

  const isLoading = Object.values(ssoState).some(state => state.loading);

  return {
    ssoState,
    isLoading,
    getSSOConfig,
    authenticateWithSSO,
    handleSSOCallback,
    handleAccountLinking,
    clearSSOError,
  };
};
